package com.sddamico.draftmachine.pages.draft

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmPanel
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.glowGlass
import com.sddamico.draftmachine.ds.shadowInset
import com.sddamico.draftmachine.ds.shadowPlate
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.IconButton
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.models.LotteryEntryResponse
import com.sddamico.draftmachine.models.LotteryStateResponse
import com.sddamico.draftmachine.util.LotteryActionResult
import com.sddamico.draftmachine.util.skipLottery
import com.sddamico.draftmachine.util.spinLottery
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import kotlin.random.Random

// ── Format step · "How the lottery works" panel (mock 2a) ──

@Composable
internal fun LotteryExplainerPanel(state: WizardState) {
  val teamCount = state.teams.size
  val drawn = state.lotterySize.coerceIn(2, maxOf(2, teamCount))
  Div(
    attrs = Modifier.padding(14.px).borderRadius(DmEffects.radiusMd)
      .border(1.px, LineStyle.Solid, DmColor.glass600)
      .backgroundColor(Color.rgba(47, 166, 168, 0.06f))
      .toAttrs()
  ) {
    SpanText("How the lottery works", Modifier.dmStamp().color(DmColor.glass300).displayBlock().margin(bottom = 10.px))
    Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs()) {
      ExplainerRow("R1 · 1–$drawn", DmColor.glass400, "Drawn by weighted lottery — you set the weights next.")
      if (drawn < teamCount) {
        ExplainerRow("R1 · ${drawn + 1}–$teamCount", DmColor.patina300, "Below the lottery line — they hold the slots you set.")
      }
      if (state.rounds > 1) {
        ExplainerRow("R2–R${state.rounds}", DmColor.textMuted, "Linear, in your set order, every round to the last pick.")
      }
    }
  }
}

@Composable
private fun ExplainerRow(label: String, labelColor: Color, text: String) {
  Div(attrs = Modifier.displayFlex().gap(10.px).alignItems(AlignItems.Baseline).toAttrs()) {
    SpanText(
      label,
      Modifier.flex("0 0 auto").minWidth(66.px).fontFamily(*DmType.mono).fontSize(11.px)
        .fontWeight(DmType.weightBold).color(labelColor),
    )
    SpanText(
      text,
      Modifier.fontFamily(*DmType.body).fontSize(13.px).lineHeight(1.45).color(DmColor.textSecondary),
    )
  }
}

// ── Lottery sub-step 1 · order, line & weights (mock 2b) ──

@Composable
internal fun LotteryWeightsStep(state: WizardState, onProcess: () -> Unit) {
  // Re-seed if the roster changed since the format was selected (only reachable pre-persistence).
  LaunchedEffect(state.teams) {
    if (state.lotteryBaseOrder.toSet() != state.teams.map { it.localId }.toSet()) state.seedLotteryDefaults()
  }

  val totalWeight = state.lotteryTotalWeight()
  StepColumn {
    StepHeading("Set the weights")
    SpanText(
      "Reorder to set the base order — it runs linear below the line and in every round after the " +
        "first. Weights set each team's share of the draw.",
      Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm).color(DmColor.textMuted).lineHeight(1.55),
    )

    // Lottery-size control.
    Div(attrs = Modifier.dmPanel().padding(14.px).displayFlex().alignItems(AlignItems.Center).gap(12.px).toAttrs()) {
      Icon("dice-five", IconWeight.Bold, Modifier.fontSize(22.px).color(DmColor.glass400))
      Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
        SpanText("Picks in the lottery", Modifier.roleH3().fontSize(14.px).displayBlock())
        SpanText(
          "Draws 1.01–1.${pad2(state.lotterySize)} · the rest hold their slots",
          Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm).color(DmColor.textMuted).displayBlock(),
        )
      }
      Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(6.px).toAttrs()) {
        IconButton(
          "minus", "Fewer lottery picks", variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
          disabled = state.lotterySize <= 2, onClick = { state.setLotterySize(state.lotterySize - 1) },
        )
        Div(attrs = Modifier.minWidth(38.px).styleModifier { property("text-align", "center") }.toAttrs()) {
          SpanText(
            "${state.lotterySize}",
            Modifier.fontFamily(*DmType.mono).fontSize(16.px).fontWeight(DmType.weightBold).color(DmColor.text),
          )
          SpanText(
            " /${state.teams.size}",
            Modifier.fontFamily(*DmType.mono).fontSize(12.px).color(DmColor.steel500),
          )
        }
        IconButton(
          "plus", "More lottery picks", variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
          disabled = state.lotterySize >= state.teams.size, onClick = { state.setLotterySize(state.lotterySize + 1) },
        )
      }
    }

    Div(
      attrs = Modifier.displayFlex().alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween)
        .gap(10.px).margin(top = 2.px).toAttrs()
    ) {
      SpanText("In the draw", Modifier.dmStamp())
      SpanText("Total weight: $totalWeight", Modifier.dmStamp().color(DmColor.glass300))
    }

    Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs()) {
      state.lotteryBaseOrder.forEachIndexed { index, localId ->
        val team = state.teamFor(localId) ?: return@forEachIndexed
        val inDraw = index < state.lotterySize
        if (index == state.lotterySize) LotteryLineDivider()
        Div(
          attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px)
            .padding(topBottom = 9.px, leftRight = 12.px)
            .border(1.px, LineStyle.Solid, DmColor.border).borderRadius(DmEffects.radiusSm)
            .backgroundColor(if (inDraw) DmColor.surfacePanel else DmColor.surfaceInset)
            .toAttrs()
        ) {
          SpanText(
            pad2(index + 1),
            Modifier.fontFamily(*DmType.mono).fontSize(12.px).fontWeight(DmType.weightBold)
              .color(DmColor.textMuted).flex("0 0 auto"),
          )
          Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
            SpanText(
              team.teamName,
              Modifier.fontFamily(*DmType.body).fontSize(13.px).fontWeight(DmType.weightSemibold)
                .color(DmColor.text).displayBlock().whiteSpace(WhiteSpace.NoWrap)
                .overflow(Overflow.Hidden).styleModifier { property("text-overflow", "ellipsis") },
            )
            if (inDraw) {
              SpanText(
                "${oddsAtNo1(state.weightOf(localId), totalWeight)}% AT NO. 1",
                Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.glass300).displayBlock(),
              )
            } else {
              SpanText(
                "HOLDS PICK 1.${pad2(index + 1)}",
                Modifier.fontFamily(*DmType.mono).fontSize(10.5.px).fontWeight(DmType.weightBold)
                  .color(DmColor.patina300).displayBlock()
                  .styleModifier { property("letter-spacing", ".1em") },
              )
            }
          }
          IconButton(
            "caret-up", "Move ${team.teamName} up", variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
            disabled = index == 0, onClick = { state.moveLotteryTeam(index, -1) },
          )
          IconButton(
            "caret-down", "Move ${team.teamName} down", variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
            disabled = index == state.lotteryBaseOrder.size - 1, onClick = { state.moveLotteryTeam(index, +1) },
          )
          if (inDraw) {
            val weight = state.weightOf(localId)
            IconButton(
              "minus", "Fewer weights for ${team.teamName}", variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
              disabled = weight <= 1, onClick = { state.adjustLotteryWeight(localId, -1) },
            )
            SpanText(
              "$weight",
              Modifier.minWidth(24.px).styleModifier { property("text-align", "center") }
                .fontFamily(*DmType.mono).fontSize(14.px).fontWeight(DmType.weightBold).color(DmColor.brass300),
            )
            IconButton(
              "plus", "More weights for ${team.teamName}", variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
              onClick = { state.adjustLotteryWeight(localId, +1) },
            )
          }
        }
      }
      if (state.lotterySize >= state.lotteryBaseOrder.size) LotteryLineDivider()
    }

    SpanText(
      "Move a team across the line to change who's in the draw — its weights travel with it. " +
        "Everything below the line, and every later round, runs in this order.",
      Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm).color(DmColor.textMuted).lineHeight(1.55),
    )
  }

  StepFooter {
    // Once entities are persisted, rounds/roster are locked — no way back past this step.
    Button(
      "Back", variant = ButtonVariant.Ghost, icon = "arrow-left",
      disabled = state.submitting || state.createdDraftId != null,
      onClick = { state.goTo(WizPhase.Format) },
    )
    Button(
      "Process the lottery", variant = ButtonVariant.Primary, icon = "dice-five",
      loading = state.submitting, onClick = onProcess,
    )
  }
}

@Composable
private fun LotteryLineDivider() {
  Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px).padding(topBottom = 6.px, leftRight = 2.px).toAttrs()) {
    LotteryLineDash()
    SpanText("Lottery line", Modifier.dmStamp().color(DmColor.glass300).flex("0 0 auto"))
    LotteryLineDash()
  }
}

@Composable
private fun LotteryLineDash() {
  Div(
    attrs = Modifier.flex(1).height(2.px).styleModifier {
      property("background", "repeating-linear-gradient(90deg, rgba(47,166,168,.7) 0 8px, transparent 8px 16px)")
    }.toAttrs()
  )
}

// ── Lottery sub-step 2 · processing (mock 2c) ──

@Composable
internal fun LotteryProcessingStep(state: WizardState, onContinue: () -> Unit) {
  val scope = rememberCoroutineScope()
  val lottery = state.lotteryState ?: return
  val done = lottery.revealedCount >= lottery.lotterySize
  val totalWeight = lotteryTotalWeight(lottery.entries)
  val draftId = lottery.draftId

  // The reel is pure theater over server-locked results, so its state stays local to this step.
  var spinning by remember { mutableStateOf(false) }
  var reelNumber by remember { mutableStateOf<Int?>(null) }

  fun reveal(skipToEnd: Boolean) {
    if (spinning || done) return
    spinning = true
    state.errorMsg = null
    scope.launch {
      when (val result = if (skipToEnd) skipLottery(draftId) else spinLottery(draftId)) {
        is LotteryActionResult.Success -> {
          if (!skipToEnd) {
            for (tick in 0 until 14) {
              reelNumber = Random.nextInt(totalWeight) + 1
              delay((40 + tick * tick * 1.4).toLong())
            }
          }
          state.lotteryState = result.state
        }
        is LotteryActionResult.Failure -> state.errorMsg = result.message
      }
      spinning = false
    }
  }

  StepColumn {
    StepHeading("Processing the lottery")

    SlotMachinePanel(
      lottery = lottery,
      done = done,
      spinning = spinning,
      displayNumber = if (spinning) reelNumber else latestDraw(lottery)?.drawnNumber,
    )

    if (!done) {
      Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs()) {
        Button(
          "Spin the wheel", variant = ButtonVariant.Primary, size = ButtonSize.Lg, icon = "arrows-clockwise",
          fullWidth = true, loading = spinning, onClick = { reveal(skipToEnd = false) },
        )
        Button(
          "Skip to completion", variant = ButtonVariant.Ghost, icon = "fast-forward",
          fullWidth = true, disabled = spinning, onClick = { reveal(skipToEnd = true) },
        )
      }
    } else {
      Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(10.px).toAttrs()) {
        Div(
          attrs = Modifier.displayFlex().gap(10.px).alignItems(AlignItems.FlexStart)
            .padding(topBottom = 12.px, leftRight = 14.px)
            .border(1.px, LineStyle.Solid, DmColor.verde600).borderRadius(DmEffects.radiusMd)
            .backgroundColor(Color.rgba(69, 140, 111, 0.08f)).toAttrs()
        ) {
          Icon("check-circle", IconWeight.Fill, Modifier.fontSize(20.px).color(DmColor.verde300).margin(top = 1.px))
          Div(attrs = Modifier.fontFamily(*DmType.body).fontSize(13.px).lineHeight(1.5).color(DmColor.textSecondary).toAttrs()) {
            SpanText("The wheel is set. ", Modifier.color(DmColor.text).fontWeight(DmType.weightSemibold))
            Text("Round 1 is drawn — next you can trade, add, or skip picks before the draft opens.")
          }
        }
        Button(
          "Continue · Adjust the order", variant = ButtonVariant.Primary, size = ButtonSize.Lg,
          icon = "arrow-right", fullWidth = true, loading = state.submitting, onClick = onContinue,
        )
      }
    }

    // Number ranges.
    Div(
      attrs = Modifier.displayFlex().alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween)
        .gap(10.px).margin(top = 6.px).toAttrs()
    ) {
      SpanText("Number ranges", Modifier.dmStamp())
      SpanText("Total weight: $totalWeight", Modifier.dmStamp().color(DmColor.steel500))
    }
    Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs()) {
      lotteryRanges(lottery.entries).forEach { range ->
        RangeRow(range, totalWeight, lottery)
      }
      staticEntries(lottery).forEach { entry ->
        StaticRangeRow(entry)
      }
    }

    // Round 1 order.
    Div(
      attrs = Modifier.displayFlex().alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween)
        .gap(10.px).margin(top = 6.px).toAttrs()
    ) {
      SpanText("Round 1 order", Modifier.dmStamp())
      SpanText(
        if (done) "LOCKED" else "${lottery.revealedCount} OF ${lottery.lotterySize} DRAWN",
        Modifier.dmStamp().color(DmColor.steel500),
      )
    }
    Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs()) {
      (1..lottery.lotterySize).forEach { slot ->
        val entry = drawnEntry(lottery, slot)
        LotteryOrderRow(
          label = "1.${pad2(slot)}",
          teamName = entry?.teamName,
          caption = entry?.let { "NO. ${pad2(it.drawnNumber ?: 0)} · DRAW $slot" },
          captionColor = DmColor.glass300,
          highlight = entry != null && slot == lottery.revealedCount && !spinning,
        )
      }
      StaticOrderRows(lottery)
    }
  }

  // Weights stay editable until the first spin locks the draw.
  if (!lottery.committed) {
    StepFooter {
      Button(
        "Back · Adjust the weights", variant = ButtonVariant.Ghost, icon = "arrow-left",
        disabled = spinning, onClick = { state.goTo(WizPhase.LotteryWeights) },
      )
    }
  }
}

// ── Lottery sub-step 3 · adjust the drawn order ──

/**
 * The LM's pass over the order the draw produced — the same editor a CUSTOM draft gets, so a
 * lottery league can settle trades and comp picks before the draft opens. [onLeave] saves in both
 * directions: the order it edits is already persisted, so holding changes in memory until Review
 * would let a refresh silently revert them.
 */
@Composable
internal fun LotteryOrderStep(state: WizardState, onLeave: (WizPhase) -> Unit) {
  StepColumn {
    StepHeading("Adjust the order")
    SpanText(
      "The draw set round 1. Trade a pick to another team, add a compensatory pick, or skip the " +
        "picks of a team that's out — or leave it exactly as drawn.",
      Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm).color(DmColor.textMuted).lineHeight(1.55),
    )
    CustomOrderEditor(state)
  }

  StepFooter {
    Button(
      "Back · The draw", variant = ButtonVariant.Ghost, icon = "arrow-left",
      disabled = state.submitting, onClick = { onLeave(WizPhase.LotteryProcessing) },
    )
    Button(
      "Next · Start", variant = ButtonVariant.Primary, icon = "arrow-right",
      loading = state.submitting, onClick = { onLeave(WizPhase.Review) },
    )
  }
}

@Composable
private fun SlotMachinePanel(
  lottery: LotteryStateResponse,
  done: Boolean,
  spinning: Boolean,
  displayNumber: Int?,
) {
  Div(
    attrs = Modifier.position(Position.Relative)
      .border(1.px, LineStyle.Solid, DmColor.borderStrong).borderRadius(DmEffects.radiusLg)
      .backgroundColor(DmColor.surfaceInset).padding(16.px)
      .displayFlex().flexDirection(FlexDirection.Column).alignItems(AlignItems.Center).gap(10.px)
      .then(if (spinning) Modifier.glowGlass() else Modifier.shadowPlate())
      .styleModifier { property("transition", "box-shadow .36s") }
      .toAttrs()
  ) {
    Rivet(top = true, left = true); Rivet(top = true, left = false)
    Rivet(top = false, left = true); Rivet(top = false, left = false)

    SpanText(
      when {
        spinning -> "DRAWING…"
        done -> "ALL ${lottery.lotterySize} DRAWS COMPLETE"
        else -> "DRAW ${lottery.revealedCount + 1} OF ${lottery.lotterySize}"
      },
      Modifier.dmStamp().color(DmColor.textMuted),
    )
    Div(
      attrs = Modifier.width(128.px).height(78.px).borderRadius(DmEffects.radiusMd)
        .border(1.px, LineStyle.Solid, DmColor.steel600)
        .displayFlex().alignItems(AlignItems.Center).justifyContent(JustifyContent.Center)
        .shadowInset()
        .styleModifier {
          property("background", "linear-gradient(180deg,#0a0d10,#161c22 32%,#161c22 68%,#0a0d10)")
        }
        .toAttrs()
    ) {
      SpanText(
        displayNumber?.let { pad2(it) } ?: "– –",
        Modifier.fontFamily(*DmType.mono).fontSize(44.px).fontWeight(DmType.weightBold)
          .color(if (spinning) DmColor.glass300 else if (displayNumber == null) DmColor.steel500 else DmColor.brass300)
          .styleModifier {
            property("letter-spacing", ".04em")
            if (spinning) property("text-shadow", "0 0 18px rgba(47,166,168,.55)")
          },
      )
    }
    if (!spinning) {
      latestDraw(lottery)?.let { draw ->
        SpanText(
          "NO. ${pad2(draw.drawnNumber ?: 0)} → ${draw.teamName.uppercase()}",
          Modifier.dmStamp().color(DmColor.glass300),
        )
      }
    }
  }
}

@Composable
private fun Rivet(top: Boolean, left: Boolean) {
  Div(
    attrs = Modifier.position(Position.Absolute).width(6.px).height(6.px)
      .styleModifier {
        property(if (top) "top" else "bottom", "7px")
        property(if (left) "left" else "right", "7px")
        property("border-radius", "50%")
        property("background", "radial-gradient(circle at 35% 30%, #9fb0bd, #4a5a66 60%, #232c33)")
      }
      .toAttrs()
  )
}

@Composable
private fun RangeRow(range: LotteryRange, totalWeight: Int, lottery: LotteryStateResponse) {
  val entry = range.entry
  val weight = entry.weight ?: 0
  val drawn = entry.drawSlot != null
  val isLatest = entry.drawSlot == lottery.revealedCount
  Div(
    attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(12.px)
      .padding(topBottom = 9.px, leftRight = 12.px)
      .border(1.px, LineStyle.Solid, DmColor.border).borderRadius(DmEffects.radiusSm)
      .backgroundColor(DmColor.surfacePanel)
      .opacity(if (drawn && !isLatest) 0.55 else 1.0)
      .styleModifier { property("transition", "opacity .36s") }
      .toAttrs()
  ) {
    RangeChip("${pad2(range.lo)}–${pad2(range.hi)}", drawn)
    Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
      SpanText(
        entry.teamName,
        Modifier.fontFamily(*DmType.body).fontSize(13.px).fontWeight(DmType.weightSemibold)
          .color(DmColor.text).displayBlock().whiteSpace(WhiteSpace.NoWrap)
          .overflow(Overflow.Hidden).styleModifier { property("text-overflow", "ellipsis") },
      )
      SpanText(
        "$weight ${if (weight == 1) "WEIGHT" else "WEIGHTS"} · ${oddsAtNo1(weight, totalWeight)}%",
        Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textMuted).displayBlock(),
      )
    }
    entry.drawSlot?.let { slot ->
      SpanText(
        "PICK 1.${pad2(slot)}",
        Modifier.fontFamily(*DmType.mono).fontSize(11.px).fontWeight(DmType.weightBold)
          .color(DmColor.verde300).styleModifier { property("letter-spacing", ".08em") },
      )
    }
  }
}

@Composable
private fun StaticRangeRow(entry: LotteryEntryResponse) {
  Div(
    attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(12.px)
      .padding(topBottom = 9.px, leftRight = 12.px)
      .border(1.px, LineStyle.Solid, DmColor.border).borderRadius(DmEffects.radiusSm)
      .backgroundColor(DmColor.surfacePanel).toAttrs()
  ) {
    RangeChip("—", drawn = false, textColor = DmColor.patina300)
    Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
      SpanText(
        entry.teamName,
        Modifier.fontFamily(*DmType.body).fontSize(13.px).fontWeight(DmType.weightSemibold)
          .color(DmColor.text).displayBlock(),
      )
      SpanText(
        "STATIC · HOLDS 1.${pad2(entry.baseOrder)}",
        Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textMuted).displayBlock(),
      )
    }
  }
}

@Composable
private fun RangeChip(text: String, drawn: Boolean, textColor: Color? = null) {
  Div(
    attrs = Modifier.flex("0 0 auto").width(64.px).padding(topBottom = 5.px)
      .styleModifier { property("text-align", "center") }
      .border(1.px, LineStyle.Solid, if (drawn) DmColor.verde600 else DmColor.borderStrong)
      .borderRadius(DmEffects.radiusSm).backgroundColor(DmColor.surfaceInset).toAttrs()
  ) {
    SpanText(
      text,
      Modifier.fontFamily(*DmType.mono).fontSize(12.px).fontWeight(DmType.weightBold)
        .color(textColor ?: if (drawn) DmColor.verde300 else DmColor.glass300),
    )
  }
}

/**
 * One round-1 order row: a filled slot when [teamName] is present, otherwise the dashed
 * "AWAITING DRAW" placeholder. Shared by the processing screen and the review panel.
 */
@Composable
private fun LotteryOrderRow(
  label: String,
  teamName: String?,
  caption: String? = null,
  captionColor: Color = DmColor.textMuted,
  highlight: Boolean = false,
) {
  val filled = teamName != null
  Div(
    attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(12.px)
      .padding(topBottom = 9.px, leftRight = 12.px)
      .border(
        1.px,
        if (filled) LineStyle.Solid else LineStyle.Dashed,
        if (highlight) DmColor.glass500 else if (filled) DmColor.border else DmColor.borderStrong,
      )
      .borderRadius(DmEffects.radiusSm)
      .then(
        if (filled) Modifier.backgroundColor(if (highlight) Color.rgba(47, 166, 168, 0.10f) else DmColor.surfacePanel)
        else Modifier
      )
      .styleModifier { property("transition", "background .36s, border-color .36s") }
      .toAttrs()
  ) {
    SpanText(
      label,
      Modifier.fontFamily(*DmType.mono).fontSize(12.px).fontWeight(DmType.weightBold)
        .color(DmColor.textMuted).flex("0 0 auto"),
    )
    if (teamName != null) {
      Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
        SpanText(
          teamName,
          Modifier.fontFamily(*DmType.body).fontSize(13.px).fontWeight(DmType.weightSemibold)
            .color(DmColor.text).displayBlock(),
        )
        if (caption != null) {
          SpanText(
            caption,
            Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(captionColor).displayBlock(),
          )
        }
      }
    } else {
      SpanText(
        "AWAITING DRAW",
        Modifier.flex(1).fontFamily(*DmType.mono).fontSize(10.5.px).fontWeight(DmType.weightBold)
          .color(DmColor.steel500).styleModifier { property("letter-spacing", ".12em") },
      )
    }
  }
}

/** The below-the-line teams as round-1 order rows (slots L+1..T). */
@Composable
private fun StaticOrderRows(lottery: LotteryStateResponse) {
  staticEntries(lottery).forEachIndexed { index, entry ->
    LotteryOrderRow(
      label = "1.${pad2(lottery.lotterySize + index + 1)}",
      teamName = entry.teamName,
      caption = "STATIC · SET BY LM",
      captionColor = DmColor.patina300,
    )
  }
}

// ── Review · "Round 1 · as drawn" panel (mock 2d) ──

@Composable
internal fun LotteryDrawnOrderPanel(lottery: LotteryStateResponse) {
  Div(
    attrs = Modifier.displayFlex().alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween)
      .gap(10.px).toAttrs()
  ) {
    SpanText("Round 1 · as drawn", Modifier.dmStamp())
    SpanText("Lottery complete", Modifier.dmStamp().color(DmColor.verde300))
  }
  Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs()) {
    (1..lottery.lotterySize).forEach { slot ->
      drawnEntry(lottery, slot)?.let { entry ->
        LotteryOrderRow(
          label = "1.${pad2(slot)}",
          teamName = entry.teamName,
          caption = "NO. ${pad2(entry.drawnNumber ?: 0)} · LOTTERY",
          captionColor = DmColor.glass300,
        )
      }
    }
    StaticOrderRows(lottery)
  }
}
